import { Field, TextInput, TextArea, ScorePicker, ChipGroup, Toggle } from './fields';
import { BODY_PARTS, CONTACT_METHODS, IMMEDIATE_REACTIONS, PRACTICE_LOCATIONS, SOURCE_TYPES } from '../lib/constants';
import type { QiCaseForm } from '../lib/types';

const selectClass =
  'w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-300';

const REFERRER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '無／不填' },
  { value: 'self', label: '我自己' },
  { value: 'practitioner', label: '施炁者' },
  { value: 'member', label: '會員' },
  { value: 'mentor', label: '導師' },
  { value: 'organization', label: '組織' },
  { value: 'other', label: '其他' },
];

export default function CaseStepFields({
  step,
  form,
  onChange,
  isOwner = false,
  practitioners = [],
}: {
  step: number;
  form: QiCaseForm;
  onChange: (patch: Partial<QiCaseForm>) => void;
  isOwner?: boolean;
  practitioners?: { user_id: string; legal_name: string | null; public_display_name?: string | null; practice_locations?: string[] }[];
}) {
  if (step === 1) {
    return (
      <div className="space-y-5">
        <Field label="施炁日期與時間" required hint="案例編號將由系統自動產生。">
          <TextInput type="datetime-local" value={form.session_datetime} onChange={(v) => onChange({ session_datetime: v })} />
        </Field>
        <Field label="施炁地點" required>
          <select value={form.location} onChange={(e) => onChange({ location: e.target.value })} className={selectClass}>
            <option value="">請選擇地點</option>
            {PRACTICE_LOCATIONS.filter((item) => item.value !== 'other').map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </Field>

        {isOwner && (
          <>
            <Field label="施炁者" required hint="像掛號選擇醫師一樣，顯示ABAA核准的半匿名名稱，例如「詹老師」。">
              <select
                value={form.practitioner_verified ? form.practitioner_user_id || '' : '__unverified__'}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__unverified__') {
                    onChange({ practitioner_verified: false, practitioner_user_id: null });
                  } else {
                    onChange({ practitioner_verified: true, practitioner_user_id: v || null });
                  }
                }}
                className={selectClass}
              >
                <option value="">請選擇施炁者</option>
                {practitioners.map((p) => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.public_display_name || p.legal_name || '施炁者'}
                  </option>
                ))}
                <option value="__unverified__">找不到，手動填寫姓名</option>
              </select>
            </Field>
            {!form.practitioner_verified && (
              <Field label="施炁者姓名" hint="未經管理員核對前，不會公開顯示其施炁者資格。">
                <TextInput value={form.practitioner_name} onChange={(v) => onChange({ practitioner_name: v })} placeholder="填寫施炁者姓名" />
              </Field>
            )}
          </>
        )}

        <Field label={isOwner ? '我的稱呼' : '受施者匿名稱呼'} required>
          <TextInput value={form.recipient_alias} onChange={(v) => onChange({ recipient_alias: v })} placeholder={isOwner ? '例如：林女士／A 先生' : '例如：林女士／A 先生'} />
        </Field>
        <Field label="聯絡方式" hint="僅供追蹤使用，不會公開。">
          <TextInput value={form.recipient_contact} onChange={(v) => onChange({ recipient_contact: v })} placeholder="Line／Email／電話（僅追蹤用）" />
        </Field>
        {!isOwner && (
          <Field label="受施者會員 Email（選填）" hint="若受施者已註冊會員，填寫其 Email 可自動綁定帳號並建立站內通知；未填寫則只能複製安全連結私下傳送。">
            <TextInput value={form.recipient_email} onChange={(v) => onChange({ recipient_email: v })} placeholder="受施者會員 Email（選填）" />
          </Field>
        )}
        <Toggle checked={form.is_first_session} onChange={(v) => onChange({ is_first_session: v })} label="是否為首次接受宇炁" />

        {isOwner && (
          <div className="border-t border-foreground-200/50 pt-4 space-y-4">
            <p className="text-xs text-foreground-400 uppercase tracking-wider">原始介紹人（選填）</p>
            <Field label="是誰介紹或鼓勵您來接受宇炁療育？" hint="介紹人和施炁者可能是同一人，也可能不同。">
              <select
                value={form.referrer_type}
                onChange={(e) => onChange({ referrer_type: e.target.value as QiCaseForm['referrer_type'] })}
                className={selectClass}
              >
                {REFERRER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            {form.referrer_type && form.referrer_type !== 'self' && (
              <Field label="介紹人姓名">
                <TextInput value={form.referrer_name} onChange={(v) => onChange({ referrer_name: v })} placeholder="介紹人姓名或名稱" />
              </Field>
            )}
            <Field label="推薦碼">
              <TextInput value={form.referral_code} onChange={(v) => onChange({ referral_code: v })} placeholder="若有推薦碼請填寫" />
            </Field>
            <Field label="其他來源說明">
              <TextInput value={form.referral_source_text} onChange={(v) => onChange({ referral_source_text: v })} placeholder="其他介紹來源說明（選填）" />
            </Field>
          </div>
        )}
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="space-y-5">
        <Field label="資料來源" hint="本階段資料的來源為何？">
          <ChipGroup
            options={SOURCE_TYPES.map((s) => s.label)}
            values={SOURCE_TYPES.filter((s) => form.pre_source_type === s.value).map((s) => s.label)}
            onChange={(labels) => {
              const matched = SOURCE_TYPES.find((s) => labels.includes(s.label));
              onChange({ pre_source_type: matched?.value || '' });
            }}
            multiple={false}
          />
        </Field>
        <Field label="本次希望處理的主要問題" required>
          <TextInput value={form.pre_main_issue} onChange={(v) => onChange({ pre_main_issue: v })} placeholder="以當事人自述為主" />
        </Field>
        <Field label="問題何時開始">
          <TextInput value={form.pre_history} onChange={(v) => onChange({ pre_history: v })} placeholder="例如：三個月前、去年底" />
        </Field>
        <Field label="身體部位（複選）">
          <ChipGroup options={BODY_PARTS} values={form.pre_body_parts} onChange={(v) => onChange({ pre_body_parts: v })} />
        </Field>
        <ScorePicker label="身體不適程度" value={form.pre_discomfort} onChange={(v) => onChange({ pre_discomfort: v })} />
        <ScorePicker label="情緒狀態" value={form.pre_emotion} onChange={(v) => onChange({ pre_emotion: v })} />
        <ScorePicker label="活動或生活功能影響" value={form.pre_function} onChange={(v) => onChange({ pre_function: v })} />
        <Field label="已知舊傷或過往事件">
          <TextArea value={form.pre_treatment} onChange={(v) => onChange({ pre_treatment: v })} placeholder="已知的舊傷、病史或相關過往事件" />
        </Field>
        <Field label="是否正在接受醫療、藥物、復健或其他療法" hint="不得要求當事人停止原有醫療或藥物。">
          <TextArea value={form.pre_note} onChange={(v) => onChange({ pre_note: v })} placeholder="記錄現行醫療或療法，無則填「無」" />
        </Field>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="施炁開始時間">
            <TextInput type="time" value={form.method_start} onChange={(v) => onChange({ method_start: v })} />
          </Field>
          <Field label="施炁結束時間">
            <TextInput type="time" value={form.method_end} onChange={(v) => onChange({ method_end: v })} />
          </Field>
        </div>
        <Field label="接觸方式">
          <ChipGroup
            options={CONTACT_METHODS}
            values={form.method_contact ? [form.method_contact] : []}
            onChange={(v) => onChange({ method_contact: v[0] || '' })}
            multiple={false}
          />
        </Field>
        <Field label="施炁位置或身體部位">
          <TextInput value={form.method_position} onChange={(v) => onChange({ method_position: v })} placeholder="施作的身體部位或位置" />
        </Field>
        <Field label="是否搭配音頻、音樂或其他設備">
          <TextInput value={form.method_audio} onChange={(v) => onChange({ method_audio: v })} placeholder="無則填「無」" />
        </Field>
        <Field label="施炁者當下觀察">
          <TextArea value={form.method_observation} onChange={(v) => onChange({ method_observation: v })} rows={3} />
        </Field>
        <Toggle checked={form.method_stopped} onChange={(v) => onChange({ method_stopped: v })} label="是否中途停止" />
        <Field label="是否出現需要特別注意的反應">
          <TextArea value={form.method_attention} onChange={(v) => onChange({ method_attention: v })} placeholder="無則填「無」" />
        </Field>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="space-y-5">
        <Field label="當下反應（複選）">
          <ChipGroup options={IMMEDIATE_REACTIONS} values={form.reactions} onChange={(v) => onChange({ reactions: v })} />
        </Field>
        {form.reactions.includes('其他') && (
          <Field label="其他反應">
            <TextInput value={form.reaction_other} onChange={(v) => onChange({ reaction_other: v })} placeholder="請描述其他反應" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="反應開始時間">
            <TextInput type="time" value={form.reaction_start} onChange={(v) => onChange({ reaction_start: v })} />
          </Field>
          <Field label="持續時間">
            <TextInput value={form.reaction_duration} onChange={(v) => onChange({ reaction_duration: v })} placeholder="例如：約 5 分鐘" />
          </Field>
        </div>
        <Field label="施炁者原始紀錄">
          <TextArea value={form.reaction_practitioner_note} onChange={(v) => onChange({ reaction_practitioner_note: v })} rows={3} />
        </Field>
        <Field label="受施者原始描述">
          <TextArea value={form.reaction_recipient_note} onChange={(v) => onChange({ reaction_recipient_note: v })} rows={3} />
        </Field>
      </div>
    );
  }

  if (step === 5) {
    return (
      <div className="space-y-5">
        <ScorePicker label="身體不適程度（施炁後）" value={form.post_discomfort} onChange={(v) => onChange({ post_discomfort: v })} />
        <ScorePicker label="情緒狀態（施炁後）" value={form.post_emotion} onChange={(v) => onChange({ post_emotion: v })} />
        <ScorePicker label="活動或生活功能（施炁後）" value={form.post_function} onChange={(v) => onChange({ post_function: v })} />
        <Field label="本人感受到的主要變化">
          <TextArea value={form.post_recipient_change} onChange={(v) => onChange({ post_recipient_change: v })} rows={3} />
        </Field>
        <Field label="施炁者觀察到的主要變化">
          <TextArea value={form.post_practitioner_change} onChange={(v) => onChange({ post_practitioner_change: v })} rows={3} />
        </Field>
        <Field label="是否出現新不適">
          <TextArea value={form.post_new_discomfort} onChange={(v) => onChange({ post_new_discomfort: v })} placeholder="無則填「無」" />
        </Field>
        <Field label="是否需要醫療或其他專業協助">
          <TextInput value={form.post_need_help} onChange={(v) => onChange({ post_need_help: v })} placeholder="無則填「無」" />
        </Field>
        <Field label="下一步建議">
          <TextArea value={form.post_next_step} onChange={(v) => onChange({ post_next_step: v })} />
        </Field>
        <Toggle checked={form.post_24h_followup} onChange={(v) => onChange({ post_24h_followup: v })} label="24 小時內是否需要再次回報" />
      </div>
    );
  }

  if (step === 7) {
    return (
      <div className="space-y-4">
        <div className="bg-foreground-50/60 rounded-lg p-4">
          <p className="text-xs text-foreground-500 leading-relaxed">
            保存本案例是建立案例的基本動作，<span className="text-foreground-700 font-medium">不需在此勾選</span>。以下為「額外用途」，預設皆不啟用——不勾選不影響案例保存。
          </p>
        </div>

        <div className="bg-foreground-50/60 rounded-lg p-4 space-y-3">
          <Toggle checked={form.followup_consent} onChange={(v) => onChange({ followup_consent: v })} label="同意系統聯絡本人進行 7／30／90 日追蹤" />
          <Toggle checked={form.internal_anonymous_use} onChange={(v) => onChange({ internal_anonymous_use: v })} label="同意以匿名方式用於內部研究與品質改善" />
          <Toggle checked={form.public_anonymous_use} onChange={(v) => onChange({ public_anonymous_use: v, media_public_use: v ? form.media_public_use : false })} label="同意以匿名文字公開為宇炁見證" />
          {form.public_anonymous_use && (
            <Toggle checked={form.media_public_use} onChange={(v) => onChange({ media_public_use: v })} label="同意公開指定照片或影片" />
          )}
          <p className="text-xs text-foreground-400 leading-relaxed">
            公開見證將移除姓名、聯絡方式、頭像、臉部及其他可辨識資料。公開仍須本人主動同意＋ABAA 或授權 Admin 人工核可。
          </p>
        </div>
      </div>
    );
  }

  return null;
}